/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { PostgrestClient, PostgrestError, PostgrestFilterBuilder } from "@supabase/postgrest-js";
import type { Database, DatabaseWithOptions } from "../../__definitions__/database-definitions";
import type { UUID } from "../../utils/uuid";
import { addSupabaseBreadcrumb } from "./breadcrumbs";

type FilterOperator =
  | "eq"
  | "neq"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "like"
  | "ilike"
  | "is"
  | "in"
  | "cs"
  | "cd"
  | "sl"
  | "sr"
  | "nxl"
  | "nxr"
  | "adj"
  | "ov"
  | "fts"
  | "plfts"
  | "phfts"
  | "wfts"
  | "not.eq"
  | "not.neq"
  | "not.gt"
  | "not.gte"
  | "not.lt"
  | "not.lte"
  | "not.like"
  | "not.ilike"
  | "not.is"
  | "not.in"
  | "not.cs"
  | "not.cd"
  | "not.sl"
  | "not.sr"
  | "not.nxl"
  | "not.nxr"
  | "not.adj"
  | "not.ov"
  | "not.fts"
  | "not.plfts"
  | "not.phfts"
  | "not.wfts";

export type Filter<T extends Record<string, unknown>> = {
  key: keyof T;
  operator: FilterOperator;
  value: unknown;
};

export type GetAllOptions<T extends Record<string, unknown>> = {
  order: { column: keyof T; dir?: "asc" | "desc" }[];
  is?: { key: keyof T; value: boolean | null }[];
  filter?: Filter<T>[];
  limit?: number;
  pageSize?: number;
};

type GetResult<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  A extends PostgrestFilterBuilder<DatabaseWithOptions["options"], any, any, any>,
> =
  A extends PostgrestFilterBuilder<
    DatabaseWithOptions["options"],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    infer B
  >
    ? B
    : never;

type GetRow<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  A extends PostgrestFilterBuilder<DatabaseWithOptions["options"], any, any, any>,
> =
  A extends PostgrestFilterBuilder<
    DatabaseWithOptions["options"],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    infer B,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  >
    ? B
    : never;

export class BaseSupabaseService<
  SchemaName extends keyof Database = "public" extends keyof Database ? "public" : keyof Database,
> {
  client: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"], SchemaName>;

  constructor(
    client: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"], SchemaName>,
  ) {
    this.client = client;
  }

  protected makePageSize(pageSize?: number, limit?: number): number {
    const size = pageSize ?? 10000;
    return limit ? Math.min(size, limit) : size;
  }

  async getAllFromSelection<
    S extends PostgrestFilterBuilder<
      DatabaseWithOptions["options"],
      Database[SchemaName],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any
    >,
  >(account_id: UUID | null, select: S, options: GetAllOptions<GetRow<S>>): Promise<Awaited<S>> {
    const { order, is, limit, filter } = options ?? {};

    const pageSize = this.makePageSize(options?.pageSize, limit);

    if (account_id) select = select.eq("account_id", account_id);

    order.map(
      (column) =>
        (select = select.order(column.column as string, {
          ascending: [undefined, "asc"].includes(column.dir),
        })),
    );

    if (is) is.map(({ key, value }) => (select = select.is(key as string, value)));
    if (limit) select = select.limit(limit);

    if (filter)
      filter.map(
        ({ key, operator, value }) => (select = select.filter(key as string, operator, value)),
      );

    let allResults: unknown[] = [];
    let start = 0;
    let r;
    do {
      r = this.log_errors(await select.range(start, start + pageSize - 1));
      start += pageSize;
      if (r.error || !r.data) return r;
      allResults = allResults.concat(r.data);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    } while (r.data.length === pageSize && (limit === undefined || r.data.length < limit));
    return {
      ...r,
      count: allResults?.length,
      data: limit === undefined ? allResults : (allResults.slice(0, limit) as GetResult<S>),
    };
  }

  protected log_errors<T>(r: T & { error?: PostgrestError | null }): T {
    if (r.error)
      addSupabaseBreadcrumb({
        message: r.error.message,
        level: "error",
        data: { details: r.error.details },
      });
    return r;
  }
}

export abstract class SupabaseService<
  T extends keyof Database["public"]["Tables"],
> extends BaseSupabaseService {
  abstract table: T;

  async insert(props: (Database["public"]["Tables"][T] & { [k: string]: never })["Insert"]) {
    const t = await this.client
      .from(this.table)
      .insert(props)
      .select<"*", Database["public"]["Tables"][T]["Row"]>()
      .single();
    return this.log_errors(t);
  }

  async delete(id: (Database["public"]["Tables"][T]["Row"] & { [k: string]: never })["id"]) {
    const { error } = this.log_errors(await this.client.from(this.table).delete().match({ id }));
    if (error) return error;
  }

  async get(id: (Database["public"]["Tables"][T]["Row"] & { [k: string]: never })["id"]) {
    return this.log_errors(
      await this.client
        .from(this.table)
        .select<"*", Database["public"]["Tables"][T]["Row"]>()
        //@ts-expect-error too strict
        .eq("id", id)
        .single(),
    );
  }

  async maybeGet(id?: (Database["public"]["Tables"][T]["Row"] & { [k: string]: never })["id"]) {
    const query = this.client
      .from(this.table)
      .select<"*", Database["public"]["Tables"][T]["Row"]>();
    //@ts-expect-error too strict
    return this.log_errors(await (id ? query.eq("id", id) : query).maybeSingle());
  }

  async upsert(
    record: (Database["public"]["Tables"][T] & { [k: string]: never })["Insert"],
    options?: { onConflict: string },
  ) {
    return this.log_errors(
      await this.client
        .from(this.table)
        .upsert(record, options)
        .select<"*", Database["public"]["Tables"][T]["Row"]>()
        .single(),
    );
  }

  async update(
    id: (Database["public"]["Tables"][T]["Row"] & { [k: string]: never })["id"],
    updateProps: (Database["public"]["Tables"][T] & { [k: string]: never })["Update"],
  ) {
    return this.log_errors(
      await this.client
        .from(this.table)
        .update(updateProps)
        .match({ id })
        .select<"*", Database["public"]["Tables"][T]["Row"]>()
        .single(),
    );
  }

  readonly getAllQuery = this._getAllQuery;
  _getAllQuery() {
    return this.client.from(this.table).select<"*", Database["public"]["Tables"][T]["Row"]>("*");
  }

  async getAll(
    account_id: UUID | null,
    options: GetAllOptions<GetResult<ReturnType<this["getAllQuery"]>>[0]>,
  ) {
    return await this.getAllFromSelection(account_id, this.getAllQuery(), options);
  }

  async count() {
    const { count, ...rest } = await this.client
      .from(this.table)
      .select("id", { count: "exact", head: true });
    if (rest.error || !count) return { ...rest, count: null };
    return { ...rest, data: count };
  }
}

export abstract class SupabaseServiceView<
  T extends keyof Database["public"]["Views"],
> extends BaseSupabaseService {
  abstract table: T;

  async insert(props: (Database["public"]["Views"][T] & { [k: string]: never })["Insert"]) {
    const t = await this.client
      .from(this.table)
      .insert(props)
      .select<"*", Database["public"]["Views"][T]["Row"]>()
      .single();
    return this.log_errors(t);
  }

  async delete(id: (Database["public"]["Views"][T]["Row"] & { [k: string]: never })["id"]) {
    const { error } = this.log_errors(await this.client.from(this.table).delete().match({ id }));
    if (error) return error;
  }

  async get(id: (Database["public"]["Views"][T]["Row"] & { [k: string]: never })["id"]) {
    return this.log_errors(
      await this.client
        .from(this.table)
        .select<"*", Database["public"]["Views"][T]["Row"]>()
        //@ts-expect-error too strict
        .eq("id", id)
        .single(),
    );
  }

  async maybeGet(id?: (Database["public"]["Views"][T]["Row"] & { [k: string]: never })["id"]) {
    const query = this.client.from(this.table).select<"*", Database["public"]["Views"][T]["Row"]>();
    //@ts-expect-error too strict
    return this.log_errors(await (id ? query.eq("id", id) : query).maybeSingle());
  }

  async upsert(
    record: (Database["public"]["Views"][T] & { [k: string]: never })["Insert"],
    options?: { onConflict: string },
  ) {
    return this.log_errors(
      await this.client
        .from(this.table)
        .upsert(record, options)
        .select<"*", Database["public"]["Views"][T]["Row"]>()
        .single(),
    );
  }

  async update(
    id: (Database["public"]["Views"][T]["Row"] & { [k: string]: never })["id"],
    updateProps: (Database["public"]["Views"][T] & { [k: string]: never })["Update"],
  ) {
    return this.log_errors(
      await this.client
        .from(this.table)
        .update(updateProps)
        .match({ id })
        .select<"*", Database["public"]["Views"][T]["Row"]>()
        .single(),
    );
  }

  readonly getAllQuery = this._getAllQuery;
  _getAllQuery() {
    return this.client.from(this.table).select<"*", Database["public"]["Views"][T]["Row"]>("*");
  }

  async getAll(
    account_id: UUID | null,
    options: GetAllOptions<GetResult<ReturnType<this["getAllQuery"]>>[0]>,
  ) {
    return await this.getAllFromSelection(account_id, this.getAllQuery(), options);
  }

  async count() {
    const { count, ...rest } = await this.client
      .from(this.table)
      .select("id", { count: "exact", head: true });
    if (rest.error || !count) return { ...rest, count: null };
    return { ...rest, data: count };
  }
}

export abstract class SupabaseCEAdminServiceView<
  T extends keyof Database["ce_admin"]["Views"],
> extends BaseSupabaseService<"ce_admin"> {
  abstract table: T;

  async insert(props: (Database["ce_admin"]["Views"][T] & { [k: string]: never })["Insert"]) {
    const t = await this.client
      .from(this.table)
      .insert(props)
      .select<"*", Database["ce_admin"]["Views"][T]["Row"]>()
      .single();
    return this.log_errors(t);
  }

  async delete(id: (Database["ce_admin"]["Views"][T]["Row"] & { [k: string]: never })["id"]) {
    const { error } = this.log_errors(await this.client.from(this.table).delete().match({ id }));
    if (error) return error;
  }

  async get(id: (Database["ce_admin"]["Views"][T]["Row"] & { [k: string]: never })["id"]) {
    return this.log_errors(
      await this.client
        .from(this.table)
        .select<"*", Database["ce_admin"]["Views"][T]["Row"]>()
        //@ts-expect-error too strict
        .eq("id", id)
        .single(),
    );
  }

  async maybeGet(id?: (Database["ce_admin"]["Views"][T]["Row"] & { [k: string]: never })["id"]) {
    const query = this.client
      .from(this.table)
      .select<"*", Database["ce_admin"]["Views"][T]["Row"]>();
    //@ts-expect-error too strict
    return this.log_errors(await (id ? query.eq("id", id) : query).maybeSingle());
  }

  async upsert(
    record: (Database["ce_admin"]["Views"][T] & { [k: string]: never })["Insert"],
    options?: { onConflict: string },
  ) {
    return this.log_errors(
      await this.client
        .from(this.table)
        .upsert(record, options)
        .select<"*", Database["ce_admin"]["Views"][T]["Row"]>()
        .single(),
    );
  }

  async update(
    id: (Database["ce_admin"]["Views"][T]["Row"] & { [k: string]: never })["id"],
    updateProps: (Database["ce_admin"]["Views"][T] & { [k: string]: never })["Update"],
  ) {
    return this.log_errors(
      await this.client
        .from(this.table)
        .update(updateProps)
        .match({ id })
        .select<"*", Database["ce_admin"]["Views"][T]["Row"]>()
        .single(),
    );
  }

  readonly getAllQuery = this._getAllQuery;
  _getAllQuery() {
    return this.client.from(this.table).select<"*", Database["ce_admin"]["Views"][T]["Row"]>("*");
  }

  async getAll(
    account_id: UUID | null,
    options: GetAllOptions<GetResult<ReturnType<this["getAllQuery"]>>[0]>,
  ) {
    return await this.getAllFromSelection(account_id, this.getAllQuery(), options);
  }

  async count() {
    const { count, ...rest } = await this.client
      .from(this.table)
      .select("id", { count: "exact", head: true });
    if (rest.error || !count) return { ...rest, count: null };
    return { ...rest, data: count };
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function noNullFieldsSingle<T>(d: T | undefined): d is
  | {
      [K in keyof T]: Exclude<T[K], null>;
    }
  | undefined {
  return true;
}

export type NoNullFields<T> = {
  [K in keyof T]: Exclude<T[K], null>;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function noNullFields<T>(d: T[] | undefined): d is NoNullFields<T>[] | undefined {
  return true;
}

export function assertNoNullFields<T>(
  d: T[] | undefined | null,
): asserts d is NoNullFields<T>[] | undefined | null {
  if (!noNullFields(d || undefined)) throw new Error("Bad data");
}
